'use client';

import type { InteractionType } from '~/model/InteractionType';
import type { ProjectType } from '~/model/ProjectType';
import { VpIcon } from '@vtmn-play/react/headless';
import { useState } from 'react';
import { getShareUrl } from '@/app/actions/server/seo';
import { dataLayerPush } from '@/app/utils/analytics/datalayer';
import { usePathname } from '@/i18n/routing';
import { checkIsProject } from '~/components/Project/utils';
import { AnalyticEvents } from '~/const/appConst';
import { ShareButton } from './ShareButton';
import { ShareDialog } from './ShareDialog';

export const Share = ({
  entity,
  iconAlone = true,
}: {
  entity: InteractionType | ProjectType;
  iconAlone?: boolean;
}) => {
  const pathname = usePathname();

  const [isSharing, setIsSharing] = useState(false);
  const [shareFailed, setShareFailed] = useState(false);
  const [dialogOpened, setDialogOpened] = useState(false);
  const handleDialog = (opening: boolean) => setDialogOpened(opening);
  const isProject = checkIsProject(entity);
  const isInteraction = !isProject;
  const hasTargettedUsers = isInteraction && entity?.is_there_targeted_users;
  const hasTargettedCriterias
    = isInteraction && entity?.target_criteria?.length > 0;
  // An interaction is condirered private only
  // if it has a targetted email without targetted criterias
  const isPrivateInteraction = hasTargettedUsers && !hasTargettedCriterias;

  // Share button will appear unconditionally on project page
  // and on interaction page if it is not private
  const canBeShared = !isPrivateInteraction || isProject;

  const handleShare = async () => {
    try {
      if (navigator?.share) {
        const url = new URL(await getShareUrl(pathname));
        url.searchParams.set('utm_medium', 'share_link');
        setIsSharing(true);
        navigator.share({ url: url.href });
      } else {
        handleDialog(true);
      }
    } catch {
      setShareFailed(true);
    } finally {
      setIsSharing(false);
      if (isProject) {
        dataLayerPush({
          event: AnalyticEvents.SHARE_PROJECT,
          page_project_name: entity.title,
        });
      }
    }
  };

  return entity && canBeShared
    ? (
        <div data-testid="interaction-share" className="interaction-share">
          <ShareButton
            iconAlone={iconAlone}
            onClick={handleShare}
            disabled={isSharing}
          >
            <VpIcon name={shareFailed ? 'warning-error' : 'share-apple'} />
          </ShareButton>
          <ShareDialog
            open={dialogOpened}
            entity={entity}
            handleShareDialogOpen={handleDialog}
          />
        </div>
      )
    : null;
};
